/**
 * BankReconciliation - Reconcile bank statements against posted journal entries
 * Two views: Start/list view and Active reconciliation view
 */

import React, { useState, useEffect, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BankReconciliationProps {
  businessId: number;
  accounts: { id: number; account_name: string; account_number: number | string; account_type?: string }[];
  currency: 'CAD' | 'USD';
}

interface ReconciliationSession {
  id: number;
  account_id: number;
  statement_date: string;
  statement_balance: number;
  status: string;
  cleared_balance: number;
  difference: number;
}

interface ReconciliationItem {
  id: number;
  description: string;
  amount: number;
  transaction_date: string;
  is_cleared: boolean;
}

interface ReconciliationReport {
  reconciliation_id: number;
  account_name: string;
  account_id: number;
  statement_date: string;
  statement_balance: number;
  status: string;
  completed_at: string | null;
  cleared_balance: number;
  uncleared_balance: number;
  difference: number;
  cleared_items: ReconciliationItem[];
  cleared_count: number;
  uncleared_items: ReconciliationItem[];
  uncleared_count: number;
  total_items: number;
}

type ViewMode = 'start' | 'active' | 'report';

// ─── API helpers ─────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const apiFetch = async <T,>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = `${API_BASE}/api/accounting${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.message || result.error || `Request failed (${response.status})`);
  }

  return result.data ?? result;
};

// ─── Currency formatter ──────────────────────────────────────────────────────

const formatMoney = (value: number, curr: 'CAD' | 'USD' = 'CAD'): string => {
  const locale = curr === 'CAD' ? 'en-CA' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  container: {
    padding: '24px',
    maxWidth: '960px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,

  heading: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '24px',
    color: '#111827',
  } as React.CSSProperties,

  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
  } as React.CSSProperties,

  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '6px',
  } as React.CSSProperties,

  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    background: '#ffffff',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  primaryButton: {
    padding: '10px 20px',
    background: '#dc2626',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,

  secondaryButton: {
    padding: '8px 16px',
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,

  disabledButton: {
    padding: '10px 20px',
    background: '#9ca3af',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'not-allowed',
    opacity: 0.6,
  } as React.CSSProperties,

  successButton: {
    padding: '10px 20px',
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,

  // errorBanner removed - using shared .error-message class from shared.css

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '14px',
  } as React.CSSProperties,

  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,

  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f3f4f6',
  } as React.CSSProperties,

  summaryBar: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap' as const,
    marginBottom: '20px',
  } as React.CSSProperties,

  summaryItem: {
    flex: '1 1 200px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  summaryLabel: {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '4px',
  } as React.CSSProperties,

  summaryValue: {
    fontSize: '20px',
    fontWeight: 700,
  } as React.CSSProperties,

  statusBadge: (status: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    background: status === 'completed' ? '#d1fae5' : status === 'in_progress' ? '#dbeafe' : '#f3f4f6',
    color: status === 'completed' ? '#059669' : status === 'in_progress' ? '#dc2626' : '#6b7280',
  }),

  formRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  formField: {
    flex: '1 1 200px',
  } as React.CSSProperties,

  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap' as const,
    gap: '8px',
  } as React.CSSProperties,

  checkboxCell: {
    width: '40px',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#dc2626',
  } as React.CSSProperties,

  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: '#dc2626',
    fontSize: '14px',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0',
    marginBottom: '16px',
    fontWeight: 500,
  } as React.CSSProperties,

  emptyState: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '14px',
  } as React.CSSProperties,

  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '12px',
  } as React.CSSProperties,
};

// ─── Component ───────────────────────────────────────────────────────────────

const BankReconciliation: React.FC<BankReconciliationProps> = ({ businessId, accounts, currency }) => {
  // Local currency formatter
  const fmt = (value: number) => formatMoney(value, currency);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('start');

  // Start view state
  const [sessions, setSessions] = useState<ReconciliationSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<number>(0);
  const [statementDate, setStatementDate] = useState(new Date().toISOString().split('T')[0]);
  const [statementBalance, setStatementBalance] = useState('');
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Active reconciliation state
  const [activeSession, setActiveSession] = useState<ReconciliationSession | null>(null);
  const [items, setItems] = useState<ReconciliationItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // Report view state
  const [reportData, setReportData] = useState<ReconciliationReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reopening, setReopening] = useState(false);

  // Add Missing Transaction state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addTxnType, setAddTxnType] = useState<'expense' | 'income'>('expense');
  const [addTxnDate, setAddTxnDate] = useState(new Date().toISOString().split('T')[0]);
  const [addTxnDescription, setAddTxnDescription] = useState('');
  const [addTxnAmount, setAddTxnAmount] = useState('');
  const [addTxnAccountId, setAddTxnAccountId] = useState<number>(0);
  const [addTxnSubmitting, setAddTxnSubmitting] = useState(false);
  const [addTxnError, setAddTxnError] = useState<string | null>(null);
  const [addTxnSuccess, setAddTxnSuccess] = useState<string | null>(null);

  // ─── Computed values ───────────────────────────────────────────────────────

  const clearedBalance = items
    .filter(item => item.is_cleared)
    .reduce((sum, item) => sum + item.amount, 0);

  const statementBalanceNum = activeSession?.statement_balance ?? 0;
  const difference = statementBalanceNum - clearedBalance;
  const isBalanced = Math.abs(difference) < 0.01;

  // ─── Data loading ──────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const data = await apiFetch<ReconciliationSession[]>(
        `/reconciliation/business/${businessId}`
      );
      setSessions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setStartError(err.message || 'Failed to load reconciliation sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, [businessId]);

  const loadItems = useCallback(async (reconId: number) => {
    try {
      setItemsLoading(true);
      setToggleError(null);
      const data = await apiFetch<ReconciliationItem[]>(
        `/reconciliation/${reconId}/items`
      );
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setToggleError(err.message || 'Failed to load reconciliation items');
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleStartReconciliation = async () => {
    setStartError(null);

    if (!selectedAccountId) {
      setStartError('Please select an account');
      return;
    }
    if (!statementDate) {
      setStartError('Please enter a statement date');
      return;
    }
    const balanceNum = parseFloat(statementBalance);
    if (isNaN(balanceNum)) {
      setStartError('Please enter a valid statement balance');
      return;
    }

    try {
      setStarting(true);
      const session = await apiFetch<ReconciliationSession>(
        `/reconciliation/business/${businessId}/start`,
        {
          method: 'POST',
          body: JSON.stringify({
            account_id: selectedAccountId,
            statement_date: statementDate,
            statement_balance: balanceNum,
          }),
        }
      );
      setActiveSession(session);
      await loadItems(session.id);
      setViewMode('active');
    } catch (err: any) {
      setStartError(err.message || 'Failed to start reconciliation');
    } finally {
      setStarting(false);
    }
  };

  const handleResumeSession = async (session: ReconciliationSession) => {
    setActiveSession(session);
    await loadItems(session.id);
    setViewMode('active');
  };

  const handleBackToList = () => {
    setViewMode('start');
    setActiveSession(null);
    setItems([]);
    setToggleError(null);
    setCompleteError(null);
    setShowAddForm(false);
    setAddTxnError(null);
    setAddTxnSuccess(null);
    loadSessions();
  };

  const handleToggleItem = async (itemId: number) => {
    if (!activeSession) return;

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const newClearedState = !item.is_cleared;

    // Optimistic update
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_cleared: newClearedState } : i));
    setToggleError(null);

    try {
      await apiFetch(`/reconciliation/${activeSession.id}/toggle`, {
        method: 'POST',
        body: JSON.stringify({ item_ids: [itemId], clear: newClearedState }),
      });
    } catch (err: any) {
      // Revert on failure
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, is_cleared: !newClearedState } : i));
      setToggleError(err.message || 'Failed to update item');
    }
  };

  const handleBulkToggle = async (clear: boolean) => {
    if (!activeSession) return;

    const targetIds = items
      .filter(item => item.is_cleared !== clear)
      .map(item => item.id);

    if (targetIds.length === 0) return;

    // Optimistic update
    const previousItems = [...items];
    setItems(prev => prev.map(item => ({ ...item, is_cleared: clear })));
    setToggleError(null);

    try {
      await apiFetch(`/reconciliation/${activeSession.id}/toggle`, {
        method: 'POST',
        body: JSON.stringify({ item_ids: targetIds, clear }),
      });
    } catch (err: any) {
      setItems(previousItems);
      setToggleError(err.message || 'Failed to update items');
    }
  };

  const handleComplete = async () => {
    if (!activeSession || !isBalanced) return;

    try {
      setCompleting(true);
      setCompleteError(null);
      await apiFetch(`/reconciliation/${activeSession.id}/complete`, {
        method: 'POST',
      });
      handleBackToList();
    } catch (err: any) {
      setCompleteError(err.message || 'Failed to complete reconciliation');
    } finally {
      setCompleting(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!activeSession) return;

    setAddTxnError(null);
    setAddTxnSuccess(null);

    if (!addTxnDate) {
      setAddTxnError('Please enter a date');
      return;
    }
    if (!addTxnDescription.trim()) {
      setAddTxnError('Please enter a description');
      return;
    }
    const amountNum = parseFloat(addTxnAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setAddTxnError('Please enter a valid amount greater than zero');
      return;
    }
    if (!addTxnAccountId) {
      setAddTxnError('Please select a category account');
      return;
    }

    try {
      setAddTxnSubmitting(true);
      await apiFetch(`/reconciliation/${activeSession.id}/add-transaction`, {
        method: 'POST',
        body: JSON.stringify({
          date: addTxnDate,
          description: addTxnDescription.trim(),
          amount: amountNum,
          type: addTxnType,
          account_id: addTxnAccountId,
          bank_account_id: activeSession.account_id,
        }),
      });

      setAddTxnSuccess('Transaction added successfully');
      setAddTxnDescription('');
      setAddTxnAmount('');
      setAddTxnAccountId(0);
      setAddTxnDate(new Date().toISOString().split('T')[0]);

      // Refresh items so the new transaction appears
      await loadItems(activeSession.id);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setAddTxnSuccess(null), 3000);
    } catch (err: any) {
      setAddTxnError(err.message || 'Failed to add transaction');
    } finally {
      setAddTxnSubmitting(false);
    }
  };

  const resetAddForm = () => {
    setShowAddForm(false);
    setAddTxnType('expense');
    setAddTxnDate(new Date().toISOString().split('T')[0]);
    setAddTxnDescription('');
    setAddTxnAmount('');
    setAddTxnAccountId(0);
    setAddTxnError(null);
    setAddTxnSuccess(null);
  };

  const handleViewReport = async (session: ReconciliationSession) => {
    try {
      setReportLoading(true);
      setReportError(null);
      const data = await apiFetch<{ data: ReconciliationReport }>(
        `/reconciliation/${session.id}/report`
      );
      // apiFetch returns result.data ?? result, so handle both shapes
      const report = (data as any).data || data;
      setReportData(report as ReconciliationReport);
      setViewMode('report');
    } catch (err: any) {
      setReportError(err.message || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleReopen = async (reconId: number) => {
    if (!window.confirm('Are you sure you want to reopen this reconciliation? It will become editable again.')) {
      return;
    }

    try {
      setReopening(true);
      await apiFetch(`/reconciliation/${reconId}/reopen`, { method: 'POST' });
      // Refresh sessions list
      loadSessions();
      // If viewing report for this rec, go back to list
      if (reportData && reportData.reconciliation_id === reconId) {
        setViewMode('start');
        setReportData(null);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to reopen reconciliation');
    } finally {
      setReopening(false);
    }
  };

  const getAccountName = (accountId: number): string => {
    const account = accounts.find(a => a.id === accountId);
    return account ? account.account_name : `Account #${accountId}`;
  };

  // ─── Start / List View ────────────────────────────────────────────────────

  const renderStartView = () => (
    <>
      {/* Start New Reconciliation */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Start New Reconciliation</div>

        {startError && (
          <div className="error-message">{startError}</div>
        )}

        <div style={styles.formRow}>
          <div style={styles.formField}>
            <label style={styles.label}>Account</label>
            <select
              style={styles.select}
              value={selectedAccountId}
              onChange={e => setSelectedAccountId(Number(e.target.value))}
            >
              <option value={0}>Select an account...</option>
              {accounts.map(acct => (
                <option key={acct.id} value={acct.id}>
                  {acct.account_number ? `${acct.account_number} - ` : ''}{acct.account_name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Statement Date</label>
            <input
              type="date"
              style={styles.input}
              value={statementDate}
              onChange={e => setStatementDate(e.target.value)}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.label}>Statement Balance</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              style={styles.input}
              value={statementBalance}
              onChange={e => setStatementBalance(e.target.value)}
            />
          </div>
        </div>

        <button
          style={starting ? styles.disabledButton : styles.primaryButton}
          onClick={handleStartReconciliation}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Start Reconciliation'}
        </button>
      </div>

      {/* Past Reconciliations */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Past Reconciliations</div>

        {sessionsLoading ? (
          <div style={styles.loading}>Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div style={styles.emptyState}>No reconciliation sessions yet. Start one above.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Account</th>
                <th style={styles.th}>Statement Date</th>
                <th style={styles.th}>Statement Balance</th>
                <th style={styles.th}>Difference</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(session => (
                <tr key={session.id}>
                  <td style={styles.td}>{getAccountName(session.account_id)}</td>
                  <td style={styles.td}>{formatDate(session.statement_date)}</td>
                  <td style={styles.td}>{fmt(session.statement_balance)}</td>
                  <td style={{
                    ...styles.td,
                    color: Math.abs(session.difference) < 0.01 ? '#059669' : '#dc2626',
                    fontWeight: 600,
                  }}>
                    {fmt(session.difference)}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(session.status)}>
                      {session.status === 'in_progress' ? 'In Progress' : session.status === 'completed' ? 'Completed' : session.status}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      {session.status === 'in_progress' && (
                        <button
                          style={styles.secondaryButton}
                          onClick={() => handleResumeSession(session)}
                        >
                          Resume
                        </button>
                      )}
                      {session.status === 'completed' && (
                        <>
                          <button
                            style={styles.secondaryButton}
                            onClick={() => handleViewReport(session)}
                          >
                            View Report
                          </button>
                          <button
                            style={{ ...styles.secondaryButton, color: '#dc2626', borderColor: '#fca5a5' }}
                            onClick={() => handleReopen(session.id)}
                            disabled={reopening}
                          >
                            {reopening ? 'Reopening...' : 'Reopen'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  // ─── Active Reconciliation View ────────────────────────────────────────────

  const renderActiveView = () => {
    if (!activeSession) return null;

    return (
      <>
        {/* Back button */}
        <button style={styles.backLink} onClick={handleBackToList}>
          &larr; Back to Reconciliations
        </button>

        {/* Header: account + date */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
            Reconciling: {getAccountName(activeSession.account_id)}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            Statement Date: {formatDate(activeSession.statement_date)}
          </div>
        </div>

        {/* Summary bar */}
        <div style={styles.summaryBar}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Statement Balance</div>
            <div style={{ ...styles.summaryValue, color: '#111827' }}>
              {fmt(statementBalanceNum)}
            </div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Cleared Balance</div>
            <div style={{ ...styles.summaryValue, color: '#059669' }}>
              {fmt(clearedBalance)}
            </div>
          </div>
          <div style={{
            ...styles.summaryItem,
            border: isBalanced ? '2px solid #059669' : '2px solid #dc2626',
            background: isBalanced ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={styles.summaryLabel}>Difference</div>
            <div style={{
              ...styles.summaryValue,
              color: isBalanced ? '#059669' : '#dc2626',
            }}>
              {fmt(difference)}
            </div>
          </div>
        </div>

        {/* Error banners */}
        {toggleError && <div className="error-message">{toggleError}</div>}
        {completeError && <div className="error-message">{completeError}</div>}

        {/* Add Missing Transaction */}
        <div style={{ marginBottom: '16px' }}>
          <button
            style={{
              ...styles.secondaryButton,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onClick={() => showAddForm ? resetAddForm() : setShowAddForm(true)}
          >
            {showAddForm ? '- Hide Form' : '+ Add Missing Transaction'}
          </button>

          {showAddForm && (
            <div style={{
              ...styles.card,
              marginTop: '12px',
              borderLeft: '3px solid #dc2626',
            }}>
              <div style={{ ...styles.sectionTitle, marginBottom: '16px' }}>
                Add Missing Transaction
              </div>

              {addTxnError && <div className="error-message">{addTxnError}</div>}
              {addTxnSuccess && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  color: '#059669',
                  fontSize: '14px',
                  marginBottom: '16px',
                }}>
                  {addTxnSuccess}
                </div>
              )}

              {/* Type toggle */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Type</label>
                <div style={{ display: 'flex', gap: '0' }}>
                  <button
                    style={{
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: 600,
                      border: '1px solid #d1d5db',
                      borderRadius: '6px 0 0 6px',
                      cursor: 'pointer',
                      background: addTxnType === 'expense' ? '#dc2626' : '#ffffff',
                      color: addTxnType === 'expense' ? '#ffffff' : '#374151',
                      borderColor: addTxnType === 'expense' ? '#dc2626' : '#d1d5db',
                    }}
                    onClick={() => { setAddTxnType('expense'); setAddTxnAccountId(0); }}
                  >
                    Expense
                  </button>
                  <button
                    style={{
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: 600,
                      border: '1px solid #d1d5db',
                      borderRadius: '0 6px 6px 0',
                      cursor: 'pointer',
                      borderLeft: 'none',
                      background: addTxnType === 'income' ? '#059669' : '#ffffff',
                      color: addTxnType === 'income' ? '#ffffff' : '#374151',
                      borderColor: addTxnType === 'income' ? '#059669' : '#d1d5db',
                    }}
                    onClick={() => { setAddTxnType('income'); setAddTxnAccountId(0); }}
                  >
                    Income
                  </button>
                </div>
              </div>

              {/* Date + Amount row */}
              <div style={styles.formRow}>
                <div style={styles.formField}>
                  <label style={styles.label}>Date</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={addTxnDate}
                    onChange={e => setAddTxnDate(e.target.value)}
                  />
                </div>
                <div style={styles.formField}>
                  <label style={styles.label}>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    style={styles.input}
                    value={addTxnAmount}
                    onChange={e => setAddTxnAmount(e.target.value)}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  placeholder="e.g. Office supplies from Staples"
                  style={styles.input}
                  value={addTxnDescription}
                  onChange={e => setAddTxnDescription(e.target.value)}
                />
              </div>

              {/* Category dropdown */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>
                  Category ({addTxnType === 'expense' ? 'Expense' : 'Revenue'} Account)
                </label>
                <select
                  style={styles.select}
                  value={addTxnAccountId}
                  onChange={e => setAddTxnAccountId(Number(e.target.value))}
                >
                  <option value={0}>Select an account...</option>
                  {accounts
                    .filter(acct => {
                      if (addTxnType === 'expense') return acct.account_type === 'expense';
                      if (addTxnType === 'income') return acct.account_type === 'revenue';
                      return false;
                    })
                    .map(acct => (
                      <option key={acct.id} value={acct.id}>
                        {acct.account_number ? `${acct.account_number} - ` : ''}{acct.account_name}
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Submit */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  style={addTxnSubmitting ? styles.disabledButton : styles.primaryButton}
                  onClick={handleAddTransaction}
                  disabled={addTxnSubmitting}
                >
                  {addTxnSubmitting ? 'Adding...' : 'Add Transaction'}
                </button>
                <button
                  style={styles.secondaryButton}
                  onClick={resetAddForm}
                  disabled={addTxnSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={styles.secondaryButton} onClick={() => handleBulkToggle(true)}>
              Select All
            </button>
            <button style={styles.secondaryButton} onClick={() => handleBulkToggle(false)}>
              Clear All
            </button>
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {items.filter(i => i.is_cleared).length} of {items.length} items cleared
          </div>
        </div>

        {/* Items table */}
        <div style={styles.card}>
          {itemsLoading ? (
            <div style={styles.loading}>Loading transactions...</div>
          ) : items.length === 0 ? (
            <div style={styles.emptyState}>
              No transactions found for this account and period.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...styles.checkboxCell }}></th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr
                    key={item.id}
                    style={{
                      background: hoveredRow === item.id ? '#f9fafb' : item.is_cleared ? '#f0fdf4' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={() => setHoveredRow(item.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => handleToggleItem(item.id)}
                  >
                    <td style={{ ...styles.td, ...styles.checkboxCell }}>
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={item.is_cleared}
                        onChange={() => {}} // handled by row click
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td style={styles.td}>{formatDate(item.transaction_date)}</td>
                    <td style={styles.td}>{item.description}</td>
                    <td style={{
                      ...styles.td,
                      textAlign: 'right',
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: item.amount >= 0 ? '#059669' : '#dc2626',
                    }}>
                      {fmt(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Complete button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button
            style={isBalanced && !completing ? styles.successButton : styles.disabledButton}
            onClick={handleComplete}
            disabled={!isBalanced || completing}
            title={!isBalanced ? 'Difference must be $0.00 to complete' : ''}
          >
            {completing ? 'Completing...' : 'Complete Reconciliation'}
          </button>
        </div>
      </>
    );
  };

  // ─── Report View ──────────────────────────────────────────────────────────

  const renderReportView = () => {
    if (reportLoading) {
      return <div style={styles.loading}>Loading report...</div>;
    }

    if (reportError) {
      return <div className="error-message">{reportError}</div>;
    }

    if (!reportData) return null;

    const isComplete = reportData.status === 'completed';

    return (
      <>
        <button style={styles.backLink} onClick={() => { setViewMode('start'); setReportData(null); }}>
          &larr; Back to Reconciliations
        </button>

        {/* Report Header */}
        <div style={{
          ...styles.card,
          borderTop: isComplete ? '3px solid #059669' : '3px solid #3b82f6',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>
                Reconciliation Report
              </div>
              <div style={{ fontSize: '15px', color: '#374151' }}>
                {reportData.account_name}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                Statement Date: {formatDate(reportData.statement_date)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={styles.statusBadge(reportData.status)}>
                {reportData.status === 'completed' ? 'Completed' : 'In Progress'}
              </span>
              {reportData.completed_at && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Completed: {new Date(reportData.completed_at).toLocaleDateString('en-CA', {
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Numbers */}
        <div style={styles.summaryBar}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Statement Balance</div>
            <div style={{ ...styles.summaryValue, color: '#111827' }}>
              {fmt(reportData.statement_balance)}
            </div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Cleared Balance</div>
            <div style={{ ...styles.summaryValue, color: '#059669' }}>
              {fmt(reportData.cleared_balance)}
            </div>
          </div>
          <div style={{
            ...styles.summaryItem,
            border: Math.abs(reportData.difference) < 0.01 ? '2px solid #059669' : '2px solid #dc2626',
            background: Math.abs(reportData.difference) < 0.01 ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={styles.summaryLabel}>Difference</div>
            <div style={{
              ...styles.summaryValue,
              color: Math.abs(reportData.difference) < 0.01 ? '#059669' : '#dc2626',
            }}>
              {fmt(reportData.difference)}
            </div>
          </div>
        </div>

        {/* Uncleared (Outstanding) Items */}
        {reportData.uncleared_items.length > 0 && (
          <div style={styles.card}>
            <div style={styles.sectionTitle}>
              Outstanding Items ({reportData.uncleared_count})
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Transactions in your books not yet cleared on the bank statement
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {reportData.uncleared_items.map((item: any) => (
                  <tr key={item.id}>
                    <td style={styles.td}>{formatDate(item.transaction_date)}</td>
                    <td style={styles.td}>{item.description}</td>
                    <td style={{
                      ...styles.td,
                      textAlign: 'right',
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: item.amount >= 0 ? '#059669' : '#dc2626',
                    }}>
                      {fmt(item.amount)}
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e5e7eb' }}>
                  <td style={styles.td} colSpan={2}>Total Outstanding</td>
                  <td style={{
                    ...styles.td,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmt(reportData.uncleared_balance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Cleared Items */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>
            Cleared Items ({reportData.cleared_count})
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Description</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {reportData.cleared_items.length === 0 ? (
                <tr>
                  <td style={styles.td} colSpan={3}>
                    <div style={styles.emptyState}>No cleared items</div>
                  </td>
                </tr>
              ) : (
                <>
                  {reportData.cleared_items.map((item: any) => (
                    <tr key={item.id}>
                      <td style={styles.td}>{formatDate(item.transaction_date)}</td>
                      <td style={styles.td}>{item.description}</td>
                      <td style={{
                        ...styles.td,
                        textAlign: 'right',
                        fontWeight: 500,
                        fontVariantNumeric: 'tabular-nums',
                        color: item.amount >= 0 ? '#059669' : '#dc2626',
                      }}>
                        {fmt(item.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, borderTop: '2px solid #e5e7eb' }}>
                    <td style={styles.td} colSpan={2}>Total Cleared</td>
                    <td style={{
                      ...styles.td,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: '#059669',
                    }}>
                      {fmt(reportData.cleared_balance)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Reopen button */}
        {isComplete && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button
              style={{ ...styles.secondaryButton, color: '#dc2626', borderColor: '#fca5a5' }}
              onClick={() => handleReopen(reportData.reconciliation_id)}
              disabled={reopening}
            >
              {reopening ? 'Reopening...' : 'Reopen Reconciliation'}
            </button>
          </div>
        )}
      </>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Bank Reconciliation</h2>
      {viewMode === 'start' && renderStartView()}
      {viewMode === 'active' && renderActiveView()}
      {viewMode === 'report' && renderReportView()}
    </div>
  );
};

export default BankReconciliation;
